/**
 * lvEfTools.js — кнопка «ФВ по маске ЛЖ (кино)»: обвязка над чистыми ядрами. [10.19.24]
 *
 * ГДЕ СТОИТ И ПОЧЕМУ. В панели «Анализ» → «Сердечно-сосудистые», строкой ниже
 * прежней «Фракции выброса». Прежняя кнопка (viewport4D.computeEF) честно
 * говорит о себе: «отношение объёмов контраста в кадре — НЕ ФВ ЛЖ: нужна маска
 * левого желудочка». Эта кнопка и есть та самая маска. Старую не убираю: она
 * работает на КТ-объёмах с контрастом, эта — на МР-кино короткой оси; у них
 * разные данные, и пусть обе говорят, что считают.
 *
 * ПОРЯДОК РАБОТЫ (он же написан врачу в окне):
 *   1. Открыть кино короткой оси, встать на СРЕДНИЙ срез (там полость круглая
 *      и большая — распространению зерна есть от чего оттолкнуться).
 *   2. Нажать кнопку и щёлкнуть в ЦЕНТР полости ЛЖ.
 *   3. Программа раскладывает серию на срезы × фазы, ведёт зерно от щёлкнутого
 *      среза к соседним центроидом маски, строит маски в двух фазах, считает
 *      объёмы методом дисков и ФВ.
 *
 * ВЫБОР ФАЗ — сказано прямо, а не спрятано: конец диастолы берётся ПЕРВОЙ фазой
 * цикла (в ретроспективном кино первый кадр стоит на зубце R — это и есть конец
 * диастолы), конец систолы — фаза с НАИМЕНЬШЕЙ суммой площадей масок по срезам.
 * Оба выбора показываются в окне номерами фаз, и врач обязан сверить их с кино.
 *
 * Вся математика — в core/lvMask.js и core/cineStack.js, проверенных фантомами;
 * здесь только Cornerstone (кадры, клик, пиксели) и окно с числами.
 */

import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { getSeriesImageIds, getAllSeries, getDICOMTags } from '../loaders/loaderManager.js';
import { getRenderingEngine, getActiveOrFirstViewportId, cornerstone } from '../core/viewportState.js';
import { маскаСреза, объёмПолости, фракцияВыброса, оговоркиФВ } from '../core/lvMask.js';
import { мирВПиксель, геометрияИзТегов, раскладкаКино, центроидМаски } from '../core/cineStack.js';

let _ac = null;
let _ждуЗерно = null;

const $ = (id) => document.getElementById(id);

function окноВывода(заголовок) {
  const фон = document.createElement('div');
  фон.style.cssText = 'position:fixed;inset:0;z-index:60000;background:rgba(5,8,14,0.72);display:flex;align-items:center;justify-content:center;padding:14px;overflow:auto;';
  const к = document.createElement('div');
  к.style.cssText = 'max-width:680px;width:100%;max-height:92vh;overflow:auto;background:#0f1420;border:1px solid #2a3546;border-radius:14px;padding:16px;color:#dbe6f5;font-size:13px;line-height:1.5;';
  к.innerHTML = `<div style="font-size:16px;font-weight:700;margin-bottom:10px;">${заголовок}</div>`;
  фон.appendChild(к);
  фон.addEventListener('click', (e) => { if (e.target === фон) фон.remove(); });
  document.body.appendChild(фон);
  return { фон, тело: к };
}

/** Пиксели кадра со шкалой (slope/intercept — у МР обычно 1/0, но не всегда). */
async function пикселиКадра(imageId) {
  const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
  const п = image.getPixelData();
  const наклон = Number.isFinite(image.slope) ? image.slope : 1;
  const сдвиг = Number.isFinite(image.intercept) ? image.intercept : 0;
  const данные = new Float32Array(п.length);
  for (let i = 0; i < п.length; i++) данные[i] = п[i] * наклон + сдвиг;
  return { данные, ширина: image.columns, высота: image.rows };
}

/** Кадры активной серии с геометрией и временем цикла. */
async function собратьКино() {
  const серии = (() => { try { const s = getAllSeries?.() || []; return Array.isArray(s) ? s : Object.values(s); } catch { return []; } })();
  if (!серии.length) return { ок: false, причина: 'исследование не открыто' };
  const мр = серии.filter((с) => String(с.modality || '').toUpperCase() === 'MR');
  const цель = (мр.length ? мр : серии)[(мр.length ? мр : серии).length - 1];
  const uid = цель.seriesUID || цель.seriesInstanceUID;
  const imageIds = getSeriesImageIds?.(uid) || [];
  if (imageIds.length < 4) return { ок: false, причина: 'в активной серии меньше четырёх кадров — это не кино' };

  const кадры = [];
  const геометрии = new Map();
  for (const id of imageIds.slice(0, 600)) {          // 600 кадров: 20 срезов × 30 фаз
    let теги = {};
    try { теги = await getDICOMTags(id); } catch (e) { log.debug('[ФВ] теги', e.message); }
    const г = геометрияИзТегов(теги);
    if (!г) continue;
    геометрии.set(id, г);
    const время = Number(теги?.['x00181060']?.value);   // TriggerTime
    const номер = Number(теги?.['x00200013']?.value);   // InstanceNumber
    кадры.push({ id, позиция: г.позиция, ряд: г.ряд, столбец: г.столбец, время: Number.isFinite(время) ? время : null, номер: Number.isFinite(номер) ? номер : 0 });
  }
  const р = раскладкаКино(кадры);
  if (!р.ок) return { ок: false, причина: р.причина };
  return { ок: true, серия: цель, раскладка: р, геометрии };
}

/** Спросить у врача точку щелчком по активному окну. */
function ждатьЗерно() {
  return new Promise((resolve) => {
    const re = getRenderingEngine?.();
    const vpId = getActiveOrFirstViewportId?.();
    const vp = re && vpId ? re.getViewport(vpId) : null;
    if (!vp || typeof vp.canvasToWorld !== 'function') { resolve({ ок: false, причина: 'нет плоского окна с изображением' }); return; }
    const элемент = vp.element;
    const текущий = (typeof vp.getCurrentImageId === 'function') ? vp.getCurrentImageId() : null;

    const снять = () => {
      элемент.removeEventListener('click', наКлик);
      document.removeEventListener('keydown', наEsc);
      элемент.style.cursor = '';
      _ждуЗерно = null;
    };
    const наКлик = (e) => {
      const r = элемент.getBoundingClientRect();
      let мир = null;
      try { мир = vp.canvasToWorld([e.clientX - r.left, e.clientY - r.top]); } catch (err) { мир = null; }
      снять();
      if (!мир) { resolve({ ок: false, причина: 'не удалось перевести щелчок в координаты' }); return; }
      resolve({ ок: true, мир, imageId: текущий });
    };
    const наEsc = (e) => { if (e.key === 'Escape') { снять(); resolve({ ок: false, причина: 'отменено' }); } };
    элемент.addEventListener('click', наКлик);
    document.addEventListener('keydown', наEsc);
    элемент.style.cursor = 'crosshair';
    _ждуЗерно = снять;
    showToast('Щёлкните в ЦЕНТР полости левого желудочка (Esc — отмена)', 'info', 6000);
  });
}

/**
 * Объём одной фазы с распространением зерна от стартового среза.
 * Возвращает и цепочку зёрен — окно показывает их, чтобы уход зерна был виден.
 */
async function объёмФазы(сетка, геометрии, фаза, стартСрез, зерноСтарта, шагСрезаММ) {
  const срезов = сетка.length;
  const кадрыФазы = new Array(срезов);
  const зёрна = new Array(срезов);
  // От стартового среза вверх и вниз: зерно соседа — центроид маски текущего.
  const порядок = [];
  for (let i = стартСрез; i < срезов; i++) порядок.push(i);
  for (let i = стартСрез - 1; i >= 0; i--) порядок.push(i);

  const зерноДля = new Map([[стартСрез, зерноСтарта]]);
  for (const i of порядок) {
    const id = сетка[i][фаза];
    const кадр = await пикселиКадра(id);
    const г = геометрии.get(id);
    кадрыФазы[i] = { ...кадр, spacing: [г.шагПикселя[1], г.шагПикселя[0]] }; // [x,y] = [между столбцами, между строками]
    const зерно = зерноДля.get(i);
    зёрна[i] = зерно || зерноСтарта;
    const и = маскаСреза(кадрыФазы[i], зёрна[i]);
    if (и.ок && и.маска) {
      const ц = центроидМаски(и.маска, кадр.ширина, кадр.высота);
      if (ц) {
        const следующие = [i + 1, i - 1].filter((j) => j >= 0 && j < срезов && !зерноДля.has(j) && порядок.includes(j));
        for (const j of следующие) {
          // Сосед по направлению обхода получает зерно только от соседа же.
          if (Math.abs(j - i) === 1) зерноДля.set(j, [ц.x, ц.y]);
        }
      }
    }
  }
  const о = объёмПолости(кадрыФазы, зёрна, { шагСрезаММ });
  return { ...о, зёрна };
}

export async function фвПоМаскеЛЖ() {
  try {
    if (_ждуЗерно) { _ждуЗерно(); }
    showToast('Читаю кадры серии и раскладываю кино…', 'info', 2500);
    const к = await собратьКино();
    if (!к.ок) { showToast('ФВ по маске: ' + к.причина, 'warning', 6000); return null; }
    const { раскладка, геометрии, серия } = к;
    if (раскладка.срезов < 3) {
      showToast(`ФВ по маске: срезов всего ${раскладка.срезов} — метод дисков по ним будет грубым; продолжаю, но это сказано в окне`, 'warning', 6000);
    }
    if (!Number.isFinite(раскладка.шагСрезаММ)) {
      showToast('ФВ по маске: у серии один срез — объём методом дисков не посчитать', 'warning', 7000);
      return null;
    }

    const з = await ждатьЗерно();
    if (!з.ок) { if (з.причина !== 'отменено') showToast('ФВ по маске: ' + з.причина, 'warning', 5000); return null; }

    // Щёлкнутый кадр → его место в сетке и пиксель зерна.
    let стартСрез = Math.floor(раскладка.сетка.length / 2);
    for (let i = 0; i < раскладка.сетка.length; i++) {
      if (раскладка.сетка[i].includes(з.imageId)) { стартСрез = i; break; }
    }
    const кадрЗерна = раскладка.сетка[стартСрез].includes(з.imageId) ? з.imageId : раскладка.сетка[стартСрез][0];
    const гЗерна = геометрии.get(кадрЗерна);
    const пикс = мирВПиксель(гЗерна, з.мир);
    if (!пикс) { showToast('ФВ по маске: у кадра нет геометрии для пересчёта щелчка', 'warning', 6000); return null; }
    const зерно = [Math.round(пикс.x), Math.round(пикс.y)];

    showToast('Строю маски по фазам — это займёт до минуты…', 'info', 3000);

    // Конец диастолы — ПЕРВАЯ фаза цикла (кадр на зубце R). Сказано в окне.
    const фазаКД = 0;
    const диастола = await объёмФазы(раскладка.сетка, геометрии, фазаКД, стартСрез, зерно, раскладка.шагСрезаММ);
    if (!диастола.ок) {
      показатьОкно({ серия, раскладка, ошибка: 'Диастола (фаза 1) не посчиталась: ' + диастола.причина, диастола });
      return null;
    }

    // Конец систолы — фаза с наименьшей СУММОЙ площадей. Перебор честный: маски
    // строятся для каждой фазы; зерно каждой фазы — то же распространение.
    let лучшая = null;
    for (let ф = 0; ф < раскладка.фаз; ф++) {
      if (ф === фазаКД) continue;
      const о = await объёмФазы(раскладка.сетка, геометрии, ф, стартСрез, зерно, раскладка.шагСрезаММ);
      if (!о.ок) continue;
      if (о.срезов !== диастола.срезов) continue;   // несравнимые фазы не участвуют
      if (!лучшая || о.объёмМЛ < лучшая.о.объёмМЛ) лучшая = { ф, о };
    }
    if (!лучшая) {
      показатьОкно({ серия, раскладка, ошибка: 'Ни одна фаза не посчиталась по тому же набору срезов, что диастола, — сравнивать нечего', диастола });
      return null;
    }

    const итог = фракцияВыброса({ диастола, систола: лучшая.о });
    показатьОкно({ серия, раскладка, диастола, систола: лучшая.о, фазаКС: лучшая.ф, итог });
    return итог;
  } catch (e) {
    log.warn('[ФВ по маске]', e.message);
    showToast('ФВ по маске не посчиталась: ' + e.message, 'error', 6000);
    return null;
  }
}

function показатьОкно({ серия, раскладка, диастола, систола, фазаКС, итог, ошибка }) {
  const w = окноВывода('ФВ по маске ЛЖ — ' + (серия?.seriesDescription || 'кино'));
  const с = [];
  с.push(`<div style="color:#9fb3cf;">Раскладка: срезов ${раскладка.срезов}, фаз ${раскладка.фаз}, шаг между срезами ${раскладка.шагСрезаММ.toFixed(1)} мм`
    + `${раскладка.поВремени ? '' : ' · порядок фаз взят по номерам кадров: триггерного времени в тегах нет'}</div>`);
  if (ошибка) {
    с.push(`<div style="margin-top:10px;padding:10px;border-radius:8px;background:rgba(120,20,20,0.5);border:1px solid #e66;color:#ffd9d9;">${ошибка}</div>`);
  }
  if (итог?.ок) {
    с.push(`<div style="margin-top:10px;font-size:22px;font-weight:800;color:#7ee787;">ФВ ${(итог.фв * 100).toFixed(0)}%</div>`);
    с.push(`<div>КДО ${итог.кдоМЛ.toFixed(0)} мл (фаза 1 — конец диастолы по зубцу R) · `
      + `КСО ${итог.ксоМЛ.toFixed(0)} мл (фаза ${фазаКС + 1} — наименьшая сумма площадей) · УО ${итог.уоМЛ.toFixed(0)} мл · срезов в счёте ${итог.срезов}</div>`);
  } else if (итог) {
    с.push(`<div style="margin-top:10px;padding:10px;border-radius:8px;background:rgba(120,20,20,0.5);border:1px solid #e66;color:#ffd9d9;">ФВ не посчитана: ${итог.причина}</div>`);
  }
  const отч = (о, имя) => {
    if (!о) return '';
    const отк = Object.entries(о.отказыПоПричинам || {}).map(([п, n]) => `${п} — ${n}`).join('; ');
    return `<div style="margin-top:6px;">${имя}: объём ${о.ок ? о.объёмМЛ.toFixed(1) + ' мл' : '—'}, срезов ${о.срезов}, отказов ${о.отказов}`
      + `${о.папиллярныхМЛ ? `, папиллярных мышц в объёме ${о.папиллярныхМЛ.toFixed(1)} мл` : ''}${отк ? `; отказы: ${отк}` : ''}</div>`;
  };
  с.push(отч(диастола, 'Диастола'));
  с.push(отч(систола, 'Систола'));
  const огов = оговоркиФВ(итог?.ок ? итог : {});
  огов.push('Конец систолы выбран программой по наименьшей сумме площадей масок — сверьте номер фазы с кино глазами.');
  с.push(`<div style="margin-top:10px;padding:9px 11px;border-radius:8px;background:rgba(60,50,10,0.5);border:1px solid #a88;color:#e8d9a8;">${огов.join('<br>')}</div>`);
  с.push('<div style="margin-top:8px;color:#8fa3c0;">Не для диагностики.</div>');

  const кнопки = document.createElement('div');
  кнопки.style.cssText = 'display:flex;gap:8px;margin-top:12px;';
  const копир = document.createElement('button');
  копир.textContent = 'Скопировать';
  копир.style.cssText = 'flex:1;padding:10px;border-radius:8px;border:1px solid #2a3546;background:#1a2332;color:#dbe6f5;';
  копир.addEventListener('click', async () => {
    const т = w.тело.innerText;
    try { await navigator.clipboard.writeText(т); showToast('Скопировано', 'success', 1500); }
    catch (e) { showToast('Выделите текст вручную', 'warning', 2500); }
  });
  const закр = document.createElement('button');
  закр.textContent = 'Закрыть';
  закр.style.cssText = копир.style.cssText;
  закр.addEventListener('click', () => w.фон.remove());
  кнопки.append(копир, закр);

  w.тело.insertAdjacentHTML('beforeend', с.join(''));
  w.тело.appendChild(кнопки);
}

export function initLvEf() {
  if (_ac) _ac.abort();
  _ac = new AbortController();
  $('btn-ef-lvmask')?.addEventListener('click', () => { фвПоМаскеЛЖ(); }, { signal: _ac.signal });
  log.info('[ФВ по маске] кнопка привязана');
}

export default { initLvEf, фвПоМаскеЛЖ };
